import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/services/supabase'

export interface SiteSettingsMap {
  site_name: string
  site_description: string
  contact_email: string
  contact_phone: string
  whatsapp_number: string
  address: string
  instagram_url: string
  facebook_url: string
  linkedin_url: string
  youtube_url: string
  meta_title: string
  meta_description: string
  google_analytics_id: string
}

const DEFAULTS: SiteSettingsMap = {
  site_name: 'Ergonlider',
  site_description: 'Consultoria em ergonomia, gestão psicossocial e inclusão de PcD.',
  contact_email: 'contato@ergonlider.com.br',
  contact_phone: '',
  whatsapp_number: (import.meta.env.VITE_WHATSAPP_NUMBER as string) || '',
  address: 'Uberlândia - MG',
  instagram_url: '',
  facebook_url: '',
  linkedin_url: '',
  youtube_url: '',
  meta_title: 'Ergonlider - Consultoria em Ergonomia e Gestão Psicossocial',
  meta_description:
    'Há 25 anos transformando ambientes de trabalho em espaços mais seguros e humanos.',
  google_analytics_id: '',
}

export const useSiteSettingsStore = defineStore('siteSettings', () => {
  const values = ref<SiteSettingsMap>({ ...DEFAULTS })
  const loaded = ref(false)
  const loading = ref(false)

  async function load(): Promise<void> {
    if (loaded.value || loading.value) return
    loading.value = true
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')

      if (error) {
        console.warn('site_settings: leitura falhou, usando defaults', error.message)
        return
      }

      const next: SiteSettingsMap = { ...DEFAULTS }
      const rows = data || []

      // 1) Legacy format (aninhado): company.*, social.*, seo.*
      const legacy = Object.fromEntries(rows.map((r) => [r.key, r.value])) as Record<string, any>
      if (legacy.company && typeof legacy.company === 'object') {
        const c = legacy.company
        if (c.name) next.site_name = String(c.name)
        if (c.tagline) next.site_description = String(c.tagline)
        if (c.email) next.contact_email = String(c.email)
        if (c.phone) next.contact_phone = String(c.phone)
        if (c.whatsapp) next.whatsapp_number = String(c.whatsapp)
        if (c.address) next.address = String(c.address)
      }
      if (legacy.social && typeof legacy.social === 'object') {
        const s = legacy.social
        if (s.instagram) next.instagram_url = String(s.instagram)
        if (s.facebook) next.facebook_url = String(s.facebook)
        if (s.linkedin) next.linkedin_url = String(s.linkedin)
        if (s.youtube) next.youtube_url = String(s.youtube)
      }
      if (legacy.seo && typeof legacy.seo === 'object') {
        const seo = legacy.seo
        if (seo.title) next.meta_title = String(seo.title)
        if (seo.description) next.meta_description = String(seo.description)
      }

      // 2) Flat format (novo) sobrescreve legacy quando presente
      for (const row of rows) {
        const key = row.key as keyof SiteSettingsMap
        if (!(key in next)) continue
        const v = row.value
        if (v == null) continue
        if (typeof v === 'string') next[key] = v
        else if (typeof v === 'number' || typeof v === 'boolean') next[key] = String(v)
      }

      values.value = next
      loaded.value = true
    } catch (e) {
      console.warn('site_settings: erro inesperado', e)
    } finally {
      loading.value = false
    }
  }

  // Derived: build full WhatsApp URL from phone digits
  const whatsappUrl = computed(() => {
    const raw = values.value.whatsapp_number?.replace(/\D/g, '') || ''
    if (!raw) return ''
    return `https://wa.me/${raw}`
  })

  const hasWhatsapp = computed(() => whatsappUrl.value.length > 0)

  return {
    values,
    loaded,
    loading,
    whatsappUrl,
    hasWhatsapp,
    load,
  }
})
